import React, { useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { formatNumber } from '../../../utils/reportHelpers';
import {
  RecurringFaults,
  SiteRollup,
  categoryLabel,
  modelLabel,
  rollupByCategory,
  rollupByModel,
  rollupBySite,
} from './types';

interface RecurringFaultsSectionProps {
  recurring: RecurringFaults;
  isMobile: boolean;
  translate: (key: string, options?: Record<string, unknown>) => string;
}

type SiteOrderBy = 'siteName' | 'count';

const emptyStateSx = { py: 6, textAlign: 'center' as const };

const chartCardSx = {
  height: '100%',
  borderRadius: 3,
  boxShadow: 'none',
  border: '1px solid',
  borderColor: 'divider',
};

const ChartEmptyState: React.FC<{ translate: RecurringFaultsSectionProps['translate'] }> = ({
  translate,
}) => (
  <Box sx={emptyStateSx}>
    <Typography variant="body2" color="text.secondary">
      {translate('aiTroubleshooting.analytics.empty', { _: 'No data for this period.' })}
    </Typography>
  </Box>
);

// Recurring-faults breakdown. The backend returns a flat faults[]; this component derives the
// per-category / per-model / per-site rollups CLIENT-SIDE (REF-08 §3.1 / §6.13) via memoised
// reducers, then renders two bar charts (category, model) plus a sortable site table.
export const RecurringFaultsSection: React.FC<RecurringFaultsSectionProps> = ({
  recurring,
  isMobile,
  translate,
}) => {
  const faults = recurring.faults || [];

  const byCategory = useMemo(() => rollupByCategory(faults), [faults]);
  const byModel = useMemo(() => rollupByModel(faults), [faults]);
  const bySite = useMemo(() => rollupBySite(faults), [faults]);

  // Chart-ready rows with a display label resolved up front (recharts dataKey reads a plain field).
  const categoryChartData = useMemo(
    () => byCategory.map((r) => ({ label: categoryLabel(translate, r.category), count: r.count })),
    [byCategory, translate],
  );
  const modelChartData = useMemo(
    () => byModel.map((r) => ({ label: modelLabel(translate, r.chargerModel), count: r.count })),
    [byModel, translate],
  );

  const [orderBy, setOrderBy] = useState<SiteOrderBy>('count');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');

  const handleSort = (property: SiteOrderBy) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortedSites = useMemo(() => {
    const rows = [...bySite];
    rows.sort((a, b) => {
      let cmp: number;
      if (orderBy === 'count') {
        cmp = a.count - b.count;
      } else {
        cmp = (a.siteName ?? '').localeCompare(b.siteName ?? '');
      }
      return order === 'asc' ? cmp : -cmp;
    });
    return rows;
  }, [bySite, orderBy, order]);

  const renderBarChart = (data: { label: string; count: number }[]) => {
    if (data.length === 0) return <ChartEmptyState translate={translate} />;
    return (
      <ResponsiveContainer width="100%" height={isMobile ? 280 : 300}>
        <BarChart data={data} layout="vertical" margin={{ top: 8, right: 16, left: 8, bottom: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
          <XAxis
            type="number"
            tick={{ fontSize: isMobile ? 10 : 12 }}
            stroke="#606060"
            tickFormatter={(value) => formatNumber(value, 0)}
            allowDecimals={false}
          />
          <YAxis
            type="category"
            dataKey="label"
            width={isMobile ? 90 : 140}
            tick={{ fontSize: isMobile ? 10 : 12 }}
            stroke="#606060"
            tickFormatter={(value) => (isMobile ? String(value).slice(0, 14) : String(value))}
          />
          <Tooltip
            formatter={(value: number) => formatNumber(value, 0)}
            contentStyle={{
              backgroundColor: '#FFFFFF',
              border: '1px solid #E0E0E0',
              borderRadius: 8,
            }}
          />
          <Bar dataKey="count" fill="#009A75" radius={[0, 6, 6, 0]} />
        </BarChart>
      </ResponsiveContainer>
    );
  };

  return (
    <Box sx={{ minWidth: 0 }}>
      {/* Two side-by-side bar charts: by category, by charger model */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, 1fr)' },
          gap: { xs: 2, sm: 3 },
          mb: { xs: 2, sm: 3 },
          minWidth: 0,
        }}
      >
        <Card sx={chartCardSx}>
          <CardContent>
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, mb: 2, fontSize: { xs: '1rem', sm: '1.25rem' } }}
            >
              {translate('aiTroubleshooting.analytics.charts.recurringByCategory', {
                _: 'Recurring faults by category',
              })}
            </Typography>
            {renderBarChart(categoryChartData)}
          </CardContent>
        </Card>

        <Card sx={chartCardSx}>
          <CardContent>
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, mb: 2, fontSize: { xs: '1rem', sm: '1.25rem' } }}
            >
              {translate('aiTroubleshooting.analytics.charts.recurringByModel', {
                _: 'Recurring faults by charger model',
              })}
            </Typography>
            {renderBarChart(modelChartData)}
          </CardContent>
        </Card>
      </Box>

      {/* Sortable site table */}
      <Card sx={chartCardSx}>
        <CardContent>
          <Typography
            variant="h6"
            sx={{ fontWeight: 600, mb: 2, fontSize: { xs: '1rem', sm: '1.25rem' } }}
          >
            {translate('aiTroubleshooting.analytics.charts.recurringBySite', {
              _: 'Recurring faults by site',
            })}
          </Typography>

          {sortedSites.length === 0 ? (
            <Box sx={emptyStateSx}>
              <Typography variant="body2" color="text.secondary">
                {translate('aiTroubleshooting.analytics.empty', { _: 'No data for this period.' })}
              </Typography>
            </Box>
          ) : isMobile ? (
            <Box sx={{ display: 'grid', gap: 1.25 }}>
              {sortedSites.map((row: SiteRollup) => (
                <Paper
                  key={row.siteId ?? '__no_site__'}
                  variant="outlined"
                  sx={{ p: 1.5, borderRadius: 1, backgroundColor: '#FAFAFA' }}
                >
                  <Typography
                    sx={{
                      fontWeight: 700,
                      mb: 0.5,
                      wordBreak: 'break-word',
                      overflowWrap: 'anywhere',
                    }}
                  >
                    {row.siteName ??
                      translate('aiTroubleshooting.analytics.unknownSite', { _: 'Unknown site' })}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {translate('aiTroubleshooting.analytics.table.count', { _: 'Count' })}:{' '}
                    {formatNumber(row.count, 0)}
                  </Typography>
                </Paper>
              ))}
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <TableSortLabel
                        active={orderBy === 'siteName'}
                        direction={orderBy === 'siteName' ? order : 'asc'}
                        onClick={() => handleSort('siteName')}
                      >
                        {translate('aiTroubleshooting.analytics.table.site', { _: 'Site' })}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right">
                      <TableSortLabel
                        active={orderBy === 'count'}
                        direction={orderBy === 'count' ? order : 'asc'}
                        onClick={() => handleSort('count')}
                      >
                        {translate('aiTroubleshooting.analytics.table.count', { _: 'Count' })}
                      </TableSortLabel>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedSites.map((row: SiteRollup) => (
                    <TableRow key={row.siteId ?? '__no_site__'} hover>
                      <TableCell sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                        {row.siteName ??
                          translate('aiTroubleshooting.analytics.unknownSite', {
                            _: 'Unknown site',
                          })}
                      </TableCell>
                      <TableCell align="right">{formatNumber(row.count, 0)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default RecurringFaultsSection;
